// @flow
import React, { Component } from 'react';
import { withTranslation, WithTranslation, Trans } from 'react-i18next';
import { connect } from 'react-redux';
import moment from 'moment';

import shareService from '../../services/share-service';
import { fetchShare } from '../../actions/shared';

import AppView from '../layouts/AppView';

import TextInput from '../shared/TextInput';
import Button from '../shared/Button';
import Text from '../shared/Typography';
import Loader from '../shared/Loader';

import illoLock from '../../assets/illustrations/share-lock.png';
import illoHelp from '../../assets/illustrations/share-question.png';
import { getRouteTFunction } from '../../i18n';

type Props = {
  match: {
    params: {
      token: string
    }
  },
  history: Object,
  shared: {
    revoked: boolean,
    expired: boolean
  },
  fetchError?: string,
  fetchShare?: any,
  loading: boolean,
  timeShared: string,
  expiryDate: string,
  location?: {
    search?: string
  }
} & WithTranslation

type State = {
  passcode: string,
  error: string,
  record: Object,
  hasGeneratedPasscode: boolean,
  verifyFailed: boolean
}

class RecordShareVerify extends Component {
  state = {
    passcode: '',
    error: '',
    records: {},
    loading: false,
    hasGeneratedPasscode: false,
    showResendButton: false,
    resendMessage: {
      type: '',
      text: ''
    },
    verifyFailed: false
  }

  state: State;
  props: Props;

  componentDidMount() {
    // Before allowing to attempt access, check if viewer has expired or been revoked
    const { token } = this.props.match.params;
    this.props.fetchShare(token).then(() => {
      const params = new URLSearchParams(this.props.location.search);
      const { shared } = this.props;
      if (shared && shared !== [] && !shared.revoked && !shared.expired) {
        const bypass = params.get('bypass');
        if (bypass) {
          this.verify(bypass);
        }
        if (localStorage.getItem(`access_token_${token}`)) {
          const routesTFunction = getRouteTFunction();
          // If user has already authenticated JWT, send them to dashboard
          shareService.checkViewerAccess(token).then((response) => {
            if (response.accessToken) {
              this.props.history.push({
                pathname: routesTFunction('redirectPaths./shared/:token/dashboard', { token }),
                search: ''
              });
            }
          });
        }
        if (params.get('generated') === 'true') {
          this.setState({ hasGeneratedPasscode: true });
        }
      }
    });
  }

  onChange = (e: SyntheticInputEvent): void => {
    this.setState({ [e.target.name]: e.target.value });
  }

  back = (): void => {
    this.props.history.goBack();
  }

  generatePasscode = () => {
    this.setState({ loading: true });
    const token = this.props.match && this.props.match.params && this.props.match.params.token;
    return shareService.sendPasscodeEmail(token).then(() => {
      this.setState({ hasGeneratedPasscode: true, loading: false, verifyFailed: false });
    });
  }

  verify = (passcode: string) => {
    const token = this.props.match && this.props.match.params && this.props.match.params.token;
    const params = new URLSearchParams(this.props.location.search);
    params.delete('bypass');
    const routesTFunction = getRouteTFunction();
    return shareService
      .verifyPasscode(token, passcode || this.state.passcode)
      .then((response) => {
        const accessToken = response.accessToken;
        localStorage.setItem(`access_token_${token}`, accessToken);
        // send to shared dashboard
        this.props.history.push({
          pathname: routesTFunction('redirectPaths./shared/:token/dashboard', { token }),
          search: `?${params.toString()}`
        });
      })
      .catch((error) => {
        // handling wrong passcode or expired passcode
        this.setState({ error, verifyFailed: true });
        this.props.history.push({
          pathname: this.props.history.location.pathname,
          search: `?${params.toString()}`
        });
      });
  }

  resendPasscode = (evt: Event): void => {
    evt.preventDefault();
    this.setState({ loading: true, resendMessage: { type: '', text: '' } });
    const { t } = this.props;
    this.generatePasscode()
      .then(() => {
        this.setState({
          loading: false,
          resendMessage: {
            text: t('resend_success'),
            type: 'success'
          }
        });
      }).catch((error) => {
        this.setState({
          error,
          loading: false,
          resendMessage: {
            text: t('resend_failure'),
            type: 'error'
          }
        });
      });
  }

  render() {
    const { shared, timeShared, fetchError, t } = this.props;
    const { hasGeneratedPasscode } = this.state;
    const invalidViewer = (
      fetchError || !shared || shared === []
      || shared.revoked || shared.expired
      || this.state.error === 'DH_ACCESS_DENIED: Unable to access record'
    );
    if (invalidViewer) {
      // link is expired or system error
      return (
        <AppView viewType='private-body'>
          <main className='content-container record-share--not-verified'>
            <img className='record-share__illo' src={illoHelp} alt={t('illo_not_verified_alt')} />
            <Text h1 align='center'>{t('record_not_found')}</Text>
            <Text paragraph className='is-subtitle' align='center'>
              {t('not_verified')}
            </Text>
          </main>
        </AppView>
      );
    } else if (!invalidViewer) {
      return (
        // link is not expired -- ask for passcode
        <AppView viewType='private-body'>
          <div className='content-container'>
            <main className='record-share__verify'>
              <img className='record-share__illo' src={illoLock} alt={t('illo_verified_alt')} />
              <Text h1 className='record-share__title' align='center'>{t('view_shared_records')}</Text>
              {!hasGeneratedPasscode &&
                <React.Fragment>
                  <Text paragraph align='center'>
                    <Trans
                      i18nKey='generate_passcode_text'
                      t={t}
                      values={{
                        date: moment(timeShared).format('LL')
                      }}
                      components={[<Text span bold />]}
                    />
                  </Text>
                  <div className='record-share__verify__container is-center'>
                    <Button
                      id='submit'
                      className='is-primary'
                      label={t('generate_passcode_label')}
                      onClick={this.generatePasscode}
                    />
                  </div>
                </React.Fragment>
              }
              {hasGeneratedPasscode &&
                <React.Fragment>
                  <Text paragraph align='center'>
                    {t('generate_passcode_done_text')}
                  </Text>
                  <div className='record-share__verify__container is-center'>
                    <TextInput
                      id='passcode'
                      name='passcode'
                      label={t('generate_passcode_done_label')}
                      onChange={this.onChange}
                      required
                    />
                    <Button
                      id='submit'
                      className='is-primary'
                      label={t('view_health_records')}
                      isDisabled={!this.state.passcode}
                      onClick={() => this.verify(this.state.passcode)}
                    />
                  </div>
                  {this.state.verifyFailed &&
                    <Text paragraph align='center' className='record-share__verify--error'>
                      {t('bad_passcode')}
                    </Text>
                  }
                </React.Fragment>
              }
              {hasGeneratedPasscode &&
                <div className='record-share__verify__container is-center'>
                  <span
                    role='button'
                    className='type-link is-center'
                    onClick={this.resendPasscode}
                    tabIndex={0}
                  >
                    <Loader isLoading={this.state.loading} />
                    {t('passcode_not_received')}
                  </span>
                  {this.state.resendMessage && this.state.resendMessage.text &&
                    <Text paragraph align='center' className={`help-text is-${this.state.resendMessage.type}`}>
                      {this.state.resendMessage.text}
                    </Text>
                  }
                </div>
              }
            </main>
          </div>
        </AppView>
      );
    }
    return null;
  }
}

const mapStateToProps = (appState) => ({
  shared: appState.shared && appState.shared.data,
  fetchError: appState.shared && appState.shared.error,
  loading: appState.shared && appState.shared.loading,
  timeShared: appState.shared && appState.shared.data && appState.shared.data.shared_at,
  expiryDate: appState.shared && appState.shared.data && appState.shared.data.expiry_date
});

const mapDispatchToProps = (dispatch) => ({
  fetchShare: ((token) => dispatch(fetchShare(token)))
});

export default withTranslation('recordShareVerify')(
  connect(mapStateToProps, mapDispatchToProps)(RecordShareVerify)
);



// WEBPACK FOOTER //
// ./src/components/pages/record-share-verify.js